<script setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserGroupsStore, useUsersStore, useReservationsStore } from '@/stores'
import { getEligibleHosts } from '@/utils/newReservationGate'

const usersStore = useUsersStore()
const { myUser, canActForMembers, pendingReservationContext } = storeToRefs(usersStore)

const userGroupsStore = useUserGroupsStore()
const { userGroups } = storeToRefs(userGroupsStore)

const reservationsStore = useReservationsStore()

const isDialogVisible = computed(() => !!pendingReservationContext.value)

const hostOptions = computed(() => getEligibleHosts({
  myUser: myUser.value,
  userGroups: userGroups.value,
  canActForMembers: canActForMembers.value,
}))

// Default selection: prefer self when eligible, otherwise the first listed host.
const owner = ref(null)
watch(pendingReservationContext, ctx => {
  if (!ctx) {
    owner.value = null
    
    return
  }
  const opts = hostOptions.value
  const selfOpt = opts.find(o => o.source === 'self')
  owner.value = (selfOpt ? selfOpt.value : (opts[0]?.value || null))
})

const selectedHost = computed(() => hostOptions.value.find(o => o.value === owner.value) || null)
const profileIncompleteForSelectedMember = computed(() => !!selectedHost.value?.profileIncomplete)

// RM-150: when the only eligible host is a non-self entry (personal-admin or
// group member), there's no decision to make beyond confirmation — but we
// still surface the dialog so the user sees who they're booking for.
const noHostsAvailable = computed(() => hostOptions.value.length === 0)

// Only nudge the host choice when there's an actual decision to make — i.e. the
// actor can book for more than one member. A single (often self-only) option
// needs no guidance, so the tip stays hidden.
const hasHostChoice = computed(() => hostOptions.value.length > 1)

const closeDialog = () => {
  usersStore.pendingReservationContext = null
}

const onConfirmation = async () => {
  if (!owner.value || profileIncompleteForSelectedMember.value) return

  // Capture before closeDialog() — closing clears pendingReservationContext,
  // and the watcher above resets owner.value to null. Reading owner.value
  // after the close would throw on .toLowerCase() below.
  const ownerEmail = owner.value
  const ctx = pendingReservationContext.value || {}
  const { restaurant, market, reservationType = 'general' } = ctx
  closeDialog()

  await usersStore.showLoadingOverlay()
  try {
    const isSelf = ownerEmail.toLowerCase() === (myUser.value?.email || '').toLowerCase()
    const userPermissions = isSelf
      ? null
      : [
        { email: ownerEmail, permission: 'owner' },
        { email: myUser.value.email, permission: 'admin' },
      ]
    const createdBy = isSelf
      ? null
      : { email: myUser.value.email, firstName: myUser.value.firstName, lastName: myUser.value.lastName }
    await reservationsStore.newDraftReservation(
      userPermissions,
      createdBy,
      restaurant || undefined,
      market || undefined,
      { reservationType },
    )
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

const onCancel = () => {
  closeDialog()
}
</script>

<template>
  <VDialog
    max-width="500"
    :model-value="isDialogVisible"
    @update:model-value="val => { if (!val) closeDialog() }"
  >
    <VCard class="text-center">
      <VCardText>
        <h6 class="text-lg font-weight-medium mb-3">
          Who is the host?
        </h6>
        <VRow v-if="!noHostsAvailable">
          <VCol cols="12">
            <VAutocomplete
              v-model="owner"
              label="Host"
              no-data-text="No eligible host"
              :items="hostOptions"
              item-title="text"
              item-value="value"
              required
              :menu-props="{ height: ($vuetify.display.mdAndDown ? 150 : 650) }"
            >
              <template #prepend-inner>
                <VIcon>mdi-magnify</VIcon>
              </template>
            </VAutocomplete>
          </VCol>
          <VCol
            v-if="hasHostChoice"
            cols="12"
            class="pt-0"
          >
            <VAlert
              type="info"
              variant="tonal"
              density="compact"
              class="text-body-2 text-start"
            >
              Tip: Member with higher profile (status / dining history) is more likely to get a faster response &amp; higher chance of acceptance.
            </VAlert>
          </VCol>
        </VRow>
        <p
          v-else
          class="text-error mt-2"
        >
          No eligible host — you don't have permission to book on behalf of anyone.
        </p>
        <div
          v-if="profileIncompleteForSelectedMember"
          class="text-caption mt-4"
        >
          *Selected member needs to complete their profile before a request can be submitted.
        </div>
      </VCardText>

      <VCardActions class="align-center justify-center gap-2">
        <VBtn
          variant="elevated"
          :disabled="!owner || profileIncompleteForSelectedMember"
          @click="onConfirmation"
        >
          Continue
        </VBtn>
        <VBtn
          color="secondary"
          variant="tonal"
          @click="onCancel"
        >
          Cancel
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
